import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

// Bottom-left drag-resize handle for centered dialogs.
// Dialog is centered via translate(-50%, -50%), so growing width/height
// expands symmetrically — dragging the handle leftward grows width,
// dragging downward grows height. Works well for centered modals.
function ResizeHandle({ targetRef }: { targetRef: React.RefObject<HTMLElement> }) {
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = targetRef.current;
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = el.getBoundingClientRect();
    const startW = rect.width;
    const startH = rect.height;
    const startX = e.clientX;
    const startY = e.clientY;
    const minW = 320;
    const minH = 240;
    const maxW = window.innerWidth * 0.98;
    const maxH = window.innerHeight * 0.95;
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);

    const move = (ev: PointerEvent) => {
      const dx = startX - ev.clientX; // drag-left => positive => grow
      const dy = ev.clientY - startY; // drag-down => positive => grow
      const w = Math.min(maxW, Math.max(minW, startW + dx));
      const h = Math.min(maxH, Math.max(minH, startH + dy));
      el.style.width = `${w}px`;
      el.style.height = `${h}px`;
      el.style.maxWidth = "none";
      el.style.maxHeight = "none";
    };
    const up = () => {
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", up);
      handle.removeEventListener("pointercancel", up);
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", up);
    handle.addEventListener("pointercancel", up);
  };
  return (
    <div
      role="separator"
      aria-label="Resize dialog"
      onPointerDown={onPointerDown}
      className="absolute bottom-1 left-1 z-10 h-4 w-4 cursor-sw-resize select-none rounded-sm opacity-60 hover:opacity-100 transition-opacity"
      style={{
        backgroundImage:
          "linear-gradient(135deg, transparent 0 30%, currentColor 30% 40%, transparent 40% 55%, currentColor 55% 65%, transparent 65% 80%, currentColor 80% 90%, transparent 90% 100%)",
        color: "hsl(var(--muted-foreground))",
      }}
    />
  );
}

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-ink-primary/30 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:transition-none motion-reduce:animate-none",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

type DialogVariant = "drawer" | "center";

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * Layout variant.
   * - "drawer" (default): slides in from the right, full-height panel. Use for forms.
   * - "center": classic centered modal. Use for confirmations, info, image previews.
   */
  variant?: DialogVariant;
  /**
   * Show a bottom-left drag handle so the user can resize the dialog freely.
   * Defaults to true for "center" variant, false for "drawer".
   */
  resizable?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, variant = "drawer", resizable, ...props }, ref) => {
  const innerRef = React.useRef<HTMLDivElement | null>(null);
  const setRefs = React.useCallback((node: HTMLDivElement | null) => {
    innerRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
  }, [ref]);
  const showHandle = resizable ?? true;

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={setRefs}
        className={cn(
          // Shared surface
          "fixed z-50 bg-surface-card text-ink-primary border border-border/40 shadow-drawer outline-none",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "motion-reduce:transition-none motion-reduce:animate-none",
          variant === "drawer" && [
            "inset-y-0 right-0 flex h-full w-full flex-col p-0",
            "sm:max-w-lg sm:rounded-l-card",
            "data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
            "duration-300 ease-out",
          ],
          variant === "center" && [
            "left-[50%] top-[50%] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 p-6 shadow-card",
            "rounded-[var(--radius-card,1.25rem)]",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "duration-200",
          ],
          className
        )}
        {...props}
      >
        {variant === "drawer" ? (
          <div className="flex h-full flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>
          </div>
        ) : (
          children
        )}
        <DialogPrimitive.Close className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-pill text-ink-secondary transition-colors hover:bg-surface-muted hover:text-ink-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/30 disabled:pointer-events-none">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
        {showHandle && <ResizeHandle targetRef={innerRef} />}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
